import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import {
  BarChart3,
  TrendingUp,
  Users,
  CheckCircle2,
  DollarSign,
  FolderOpen,
  Mail,
  Loader2,
  Lock,
  Crown,
} from "lucide-react";
import CoordinatorEarnings from "../components/reports/CoordinatorEarnings";
import { ReportsFilterBar } from "../components/reports/ReportsFilterBar";
import { PipelineSection } from "../components/reports/PipelineSection";
import { ClosingMetricsSection } from "../components/reports/ClosingMetricsSection";
import { TaskHealthSection } from "../components/reports/TaskHealthSection";
import { FinancialSection } from "../components/reports/FinancialSection";
import { CoordinatorPerformanceSection } from "../components/reports/CoordinatorPerformanceSection";
import { DocumentTrackingSection } from "../components/reports/DocumentTrackingSection";
import { CommunicationSection } from "../components/reports/CommunicationSection";
import { AgentDirectorySection } from "../components/reports/AgentDirectorySection";

const Reports = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeFrom, setActiveFrom] = useState("");
  const [activeTo, setActiveTo] = useState("");
  const [selectedCoord, setSelectedCoord] = useState("all");
  const [activeCoord, setActiveCoord] = useState("all");
  const [coordinators, setCoordinators] = useState([]);

  const hasAccess = ["team", "enterprise"].includes(user?.plan_id) || ["admin", "super_admin"].includes(user?.role);

  const fetchData = async (from = "", to = "", coordId = "all") => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (coordId && coordId !== "all") params.coordinator_id = coordId;
      const res = await axios.get(`${API}/reports/analytics`, { withCredentials: true, params });
      setData(res.data);
      if (res.data.coordinators) setCoordinators(res.data.coordinators);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasAccess) {
      fetchData(activeFrom, activeTo, activeCoord);
    }
  }, [hasAccess, activeFrom, activeTo, activeCoord]);

  const applyDateFilter = () => {
    setActiveFrom(dateFrom);
    setActiveTo(dateTo);
    setActiveCoord(selectedCoord);
  };

  const clearDateFilter = () => {
    setDateFrom("");
    setDateTo("");
    setActiveFrom("");
    setActiveTo("");
    setSelectedCoord("all");
    setActiveCoord("all");
  };

  const setPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    const f = from.toISOString().split("T")[0];
    const t = to.toISOString().split("T")[0];
    setDateFrom(f);
    setDateTo(t);
    setActiveFrom(f);
    setActiveTo(t);
  };

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="reports-upgrade-gate">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--t-text)", fontFamily: "var(--t-heading-font)" }}>
            Team Dashboard & Reports
          </h2>
          <p className="text-sm mb-6" style={{ color: "var(--t-text-muted)" }}>
            Advanced analytics, pipeline reports, and team performance metrics are available on the <strong>Team</strong> plan and above.
          </p>
          <button
            onClick={() => navigate("/settings")}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white"
            style={{ background: "var(--t-primary)" }}
            data-testid="reports-upgrade-btn"
          >
            <Crown className="w-4 h-4" /> Upgrade to Team
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--t-primary)" }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p style={{ color: "var(--t-text-muted)" }}>Failed to load analytics data.</p>
      </div>
    );
  }

  const { pipeline, closing_metrics, coordinator_performance, task_health, financial, document_tracking, communication } = data;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-8" data-testid="reports-page" id="reports-top">
      {/* Page Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "var(--t-text)", fontFamily: "var(--t-heading-font)" }} data-testid="reports-title">
              Reports & Analytics
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--t-text-muted)" }}>
              Comprehensive overview of your transaction pipeline
            </p>
          </div>
          <p className="text-[11px]" style={{ color: "var(--t-text-subtle)" }}>
            Updated {new Date(data.generated_at).toLocaleString()}
          </p>
        </div>

        {/* Section Jump Nav */}
        <nav className="sticky top-0 z-20 -mx-6 px-6 py-2 flex flex-wrap gap-1.5 backdrop-blur-md" style={{ background: "var(--t-bg-transparent, rgba(255,255,255,0.85))", borderBottom: "1px solid var(--t-border-light)" }} data-testid="reports-section-nav">
          {[
            { id: "pipeline", icon: BarChart3, label: "Pipeline" },
            { id: "closing", icon: TrendingUp, label: "Closing" },
            { id: "tasks", icon: CheckCircle2, label: "Tasks" },
            { id: "financial", icon: DollarSign, label: "Financial" },
            { id: "coordinators", icon: Users, label: "Coordinators" },
            { id: "earnings", icon: DollarSign, label: "Earnings" },
            { id: "documents", icon: FolderOpen, label: "Documents" },
            { id: "email", icon: Mail, label: "Email" },
            { id: "agents", icon: Users, label: "Agents" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => {
                const el = document.getElementById(`section-${s.id}`);
                if (el) {
                  const offset = 52;
                  const top = el.getBoundingClientRect().top + window.scrollY - offset;
                  window.scrollTo({ top, behavior: "smooth" });
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors hover:text-white"
              style={{ background: "var(--t-card)", border: "1px solid var(--t-border)", color: "var(--t-text-muted)" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--t-primary)"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "var(--t-primary)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "var(--t-card)"; e.currentTarget.style.color = "var(--t-text-muted)"; e.currentTarget.style.borderColor = "var(--t-border)"; }}
              data-testid={`nav-${s.id}`}
            >
              <s.icon className="w-3 h-3" />
              {s.label}
            </button>
          ))}
        </nav>

        {/* Date Range Filter */}
        <ReportsFilterBar
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          selectedCoord={selectedCoord}
          setSelectedCoord={setSelectedCoord}
          activeFrom={activeFrom}
          activeTo={activeTo}
          activeCoord={activeCoord}
          setActiveCoord={setActiveCoord}
          coordinators={coordinators}
          onApply={applyDateFilter}
          onClear={clearDateFilter}
          onPreset={setPreset}
        />
      </div>

      <PipelineSection pipeline={pipeline} />
      <ClosingMetricsSection closing_metrics={closing_metrics} pipeline={pipeline} />
      <TaskHealthSection task_health={task_health} />
      <FinancialSection financial={financial} />
      <CoordinatorPerformanceSection coordinator_performance={coordinator_performance} />

      <div id="section-earnings">
        <CoordinatorEarnings />
      </div>

      <DocumentTrackingSection document_tracking={document_tracking} />
      <CommunicationSection communication={communication} />
      <AgentDirectorySection />
    </div>
  );
};

export default Reports;
